import {
    FULL_TAG_ATTENTION,
    FULL_TAG_BLING_SHORT,
    FULL_TAG_SUGGEST,
    FULL_TAG_URGENT,
    NormalizeFullParams,
} from '../../constants/fullShipment';
import { SumLastDaysFromAnchor } from './SumWindow';

/**
 * Envio ao Full — derive puro em 2 etapas, porque o `bling` pode ser
 * SOBRESCRITO depois do Selector (index.jsx aplica a seleção de depósitos):
 *
 *   1. FullDemand(dailyByDate, ctx, params)  → ritmo de venda (só série)
 *   2. FullShipment(demand + estoques, params) → cobertura/necessidade/tags
 *
 * O Selector roda as duas; quando o bling muda, index.jsx re-roda SÓ a 2ª
 * via `RecomputeFullForRow` — sem tocar na série.
 *
 * Regras completas em `constants/fullShipment.js`.
 */

/**
 * Ritmo de venda pro Full. Usa SEMPRE janelas voltando do anchor (igual
 * `Coverage`), não o período inteiro — é métrica de "ritmo recente".
 *
 * A janela é limitada ao período carregado (`ctx.periodDays`): com preset de
 * 7 dias e janela de 30, a média sai sobre 7 dias reais em vez de dividir
 * 7 dias de venda por 30 (subestimaria a demanda em 4x).
 *
 * @returns {{fullAvgDay: number, fullHasHistory: boolean}}
 */
export function FullDemand(dailyByDate, ctx, params) {
    const p = NormalizeFullParams(params);
    const anchor = ctx?.anchorDateIso;
    if (!anchor) {
        return { fullAvgDay: 0, fullHasHistory: false };
    }
    const periodDays = Number(ctx?.periodDays || 0);
    const windowDays = periodDays > 0 ? Math.min(p.avgWindowDays, periodDays) : p.avgWindowDays;
    const sold = SumLastDaysFromAnchor(dailyByDate, anchor, windowDays);
    const fullAvgDay = windowDays > 0 ? sold / windowDays : 0;
    const historyUnits = SumLastDaysFromAnchor(dailyByDate, anchor, p.historyDays);
    return { fullAvgDay, fullHasHistory: historyUnits > 0 };
}

/**
 * Cobertura, necessidade, sugestão e tags do Full.
 *
 *   alvo        = ceil(avgDay × (minDays + leadDays))
 *   fullNeed    = max(0, alvo − mlTotal)
 *   fullSuggest = min(fullNeed, bling)        "o máximo que der"
 *   fullCoverage= mlTotal / avgDay            null = sem demanda (sem tier)
 *
 * @param {{fullAvgDay: number, fullHasHistory: boolean, bling: number, mlTotal: number}} input
 * @returns {{fullCoverage: number|null, fullNeed: number, fullSuggest: number, fullTags: string[]}}
 */
export function FullShipment({ fullAvgDay, fullHasHistory, bling, mlTotal }, params) {
    const p = NormalizeFullParams(params);
    const avgDay = Math.max(0, Number(fullAvgDay) || 0);
    const stockFull = Math.max(0, Number(mlTotal) || 0);
    const stockBling = Math.max(0, Number(bling) || 0);

    const targetUnits = Math.ceil(avgDay * (p.minDays + p.leadDays));
    const fullNeed = Math.max(0, targetUnits - stockFull);
    const fullSuggest = Math.min(fullNeed, stockBling);
    const fullCoverage = avgDay > 0 ? stockFull / avgDay : null;

    const fullTags = [];
    // Tiers por cobertura — só com demanda ativa. Full zerado com venda
    // recente cai em "urgente" (cobertura 0) E, se houver Bling, também em
    // "sugestao" — as duas dizem coisas diferentes (prioridade × dá pra enviar).
    if (fullCoverage !== null) {
        if (fullCoverage < p.urgentDays) {
            fullTags.push(FULL_TAG_URGENT);
        } else if (fullCoverage < p.attentionDays) {
            fullTags.push(FULL_TAG_ATTENTION);
        }
    }
    if (stockFull === 0 && stockBling > 0 && fullHasHistory) {
        fullTags.push(FULL_TAG_SUGGEST);
    }
    if (fullNeed > 0 && fullNeed > stockBling) {
        fullTags.push(FULL_TAG_BLING_SHORT);
    }

    return { fullCoverage, fullNeed, fullSuggest, fullTags };
}

/**
 * Re-aplica a etapa 2 num row já construído, com outro `bling` (seleção de
 * depósitos). `row.tags` (auto + manuais) fica intacto — as tags do Full
 * vivem em `row.fullTags` (coluna "Análise Full"). Puro: devolve row novo.
 */
export function RecomputeFullForRow(row, bling, params) {
    const full = FullShipment(
        { fullAvgDay: row.fullAvgDay, fullHasHistory: row.fullHasHistory, bling, mlTotal: row.mlTotal },
        params,
    );
    return {
        ...row,
        bling,
        fullCoverage: full.fullCoverage,
        fullNeed: full.fullNeed,
        fullSuggest: full.fullSuggest,
        fullTags: full.fullTags,
    };
}
